import { APIContext, SearchConditionContext } from '@/App'
import { LIMIT } from '@/constants/ConnectionSettings'
import { useModal } from '@/customHooks/useModal'
import { useContext, useRef } from 'react'
import { EditSearchConditionModal } from '@/views/modal/EditSearchConditionModal'
import { SearchCondition } from '@/types/Types'
import {
  MAP_BOSS_PROBABILITIES,
  MAP_NAME1_LIST,
  MAP_NAME2_LIST,
  MAP_NAME3_LIST,
  MAP_TYPE_PROBABILITIES
} from '@/constants/TMapBasicConstants'
import { ComparisonOperators } from '@/constants/ComparisonOperators'
import { OnlyEnemies } from '@/constants/OnlyEnemyConstants'
import { ConnectionStatus } from '@/constants/ConnectionStatus'

const SearchConditionView = () => {
  // #region フック

  const api = useContext(APIContext)

  const searchCondition = useContext(SearchConditionContext)

  const addSearchConditionModal = useModal()

  const editUuid = useRef('')

  // #endregion フック

  // #region スタイル

  const searchConditionAreaStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex'
  }

  const searchConditionTableStyle: React.CSSProperties = {
    marginTop: 'auto',
    marginBottom: 'auto',
    marginLeft: '0',
    backgroundColor: '#FFFFFF',
    width: '600px',
    height: '190px',
    border: 'solid 1px',
    overflowY: 'scroll'
  }

  const buttonStyle: React.CSSProperties = {
    marginTop: '24px',
    marginLeft: '10px',
    display: 'flex',
    flexDirection: 'column'
  }

  const addButtonStyle: React.CSSProperties = {
    marginBottom: '10px'
  }

  const searchButtonStyle: React.CSSProperties = {
    marginTop: 'auto',
    marginBottom: '9px'
  }

  // #endregion スタイル

  // #region イベント

  const onClickAddButton = () => {
    editUuid.current = ''
    addSearchConditionModal.openModal()
  }

  const onClickClearButton = () => {
    const res = window.confirm('検索条件をクリアしてもよろしいですか？')
    if (res) {
      searchCondition.clear()
    }
  }

  const onClickSearchButton = () => {
    api.fetchAPIData(`searchTMapInfo.php?${searchCondition.getParams()}&limit=${LIMIT}&offset=0`)
  }

  // #endregion イベント

  const isConnecting = api.status === ConnectionStatus.CONNECTING

  return (
    <div style={searchConditionAreaStyle}>
      <div>
        検索条件：
        <div style={searchConditionTableStyle}>
          <SearchConditionTableView
            searchCondition={searchCondition}
            editUuid={editUuid}
            addSearchConditionModal={addSearchConditionModal}
          />
        </div>
      </div>
      <div style={buttonStyle}>
        <button style={addButtonStyle} onClick={onClickAddButton} disabled={isConnecting}>
          追加
        </button>
        <button onClick={onClickClearButton} disabled={isConnecting}>
          クリア
        </button>
        <button style={searchButtonStyle} onClick={onClickSearchButton} disabled={isConnecting}>
          検索
        </button>
      </div>
      <EditSearchConditionModal
        isOpen={addSearchConditionModal.isOpen}
        closeModal={addSearchConditionModal.closeModal}
        editUuid={editUuid.current}
      />
    </div>
  )
}

export default SearchConditionView

type SearchConditionProps = {
  searchCondition: SearchCondition
  editUuid: React.MutableRefObject<string>
  addSearchConditionModal: {
    isOpen: boolean
    openModal: () => void
    closeModal: () => void
  }
}

const SearchConditionTableView = ({
  searchCondition,
  editUuid,
  addSearchConditionModal
}: SearchConditionProps) => {
  // #region スタイル

  const disabledFontStyle: React.CSSProperties = {
    textDecoration: 'line-through',
    color: '#808080'
  }

  // #endregion スタイル

  // #region イベント

  const onChangeCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    searchCondition.setEnabled(e.currentTarget.value, e.currentTarget.checked)
  }

  const onClickEditButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    editUuid.current = e.currentTarget.value
    addSearchConditionModal.openModal()
  }

  const onClickDeleteButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    const res = window.confirm('指定した検索条件を削除してもよろしいですか？')
    if (res) {
      searchCondition.remove(e.currentTarget.value)
    }
  }

  // #endregion イベント

  const formattedList = searchCondition.conditionList.map((s) => {
    switch (s.key) {
      case 'mapRank[]':
        return {
          uuid: s.uuid,
          key: '地図Rank',
          value1: `0x${s.value.split('_')[0]}`,
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'mapSeed[]':
        return {
          uuid: s.uuid,
          key: '地図Seed',
          value1: `0x${s.value.split('_')[0]}`,
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'mapName1Index[]':
        return {
          uuid: s.uuid,
          key: '地図名1',
          value1: `「${MAP_NAME1_LIST[parseInt(s.value.split('_')[0])]}」`,
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'mapName2Index[]':
        return {
          uuid: s.uuid,
          key: '地図名2',
          value1: `「${MAP_NAME2_LIST[parseInt(s.value.split('_')[0])]}」`,
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'mapName3Index[]':
        return {
          uuid: s.uuid,
          key: '地図名3',
          value1: `「${MAP_NAME3_LIST[parseInt(s.value.split('_')[0])]}」`,
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'mapLevel[]':
        return {
          uuid: s.uuid,
          key: '地図Lv',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'places[]':
        return {
          uuid: s.uuid,
          key: '場所候補',
          value1: `0x${parseInt(s.value).toString(16).toUpperCase().padStart(2, '0')}`,
          value2: 'を含む',
          enabled: s.enabled
        }
      case 'mapTypeIndex[]':
        return {
          uuid: s.uuid,
          key: '地形',
          value1: MAP_TYPE_PROBABILITIES[parseInt(s.value.split('_')[0])].name,
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'floorCount[]':
        return {
          uuid: s.uuid,
          key: '深さ',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'b1fEnemyRank[]':
        return {
          uuid: s.uuid,
          key: 'B1F敵ランク',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'bossIndex[]':
        return {
          uuid: s.uuid,
          key: 'ボス',
          value1: MAP_BOSS_PROBABILITIES[parseInt(s.value.split('_')[0])].name,
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxS[]':
        return {
          uuid: s.uuid,
          key: 'S箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxA[]':
        return {
          uuid: s.uuid,
          key: 'A箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxB[]':
        return {
          uuid: s.uuid,
          key: 'B箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxC[]':
        return {
          uuid: s.uuid,
          key: 'C箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxD[]':
        return {
          uuid: s.uuid,
          key: 'D箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxE[]':
        return {
          uuid: s.uuid,
          key: 'E箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxF[]':
        return {
          uuid: s.uuid,
          key: 'F箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxG[]':
        return {
          uuid: s.uuid,
          key: 'G箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxH[]':
        return {
          uuid: s.uuid,
          key: 'H箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxI[]':
        return {
          uuid: s.uuid,
          key: 'I箱の数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'boxTotalCount[]':
        return {
          uuid: s.uuid,
          key: '宝総数',
          value1: parseInt(s.value.split('_')[0]),
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'isNoEnemy[]':
        return {
          uuid: s.uuid,
          key: '敵無',
          value1: parseInt(s.value.split('_')[0]) === 1 ? '有り' : '無し',
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'isOnly[]':
        return {
          uuid: s.uuid,
          key: 'オンリー',
          value1: parseInt(s.value.split('_')[0]) === 1 ? '有り' : '無し',
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'isFewEnemy[]':
        return {
          uuid: s.uuid,
          key: '敵減',
          value1: parseInt(s.value.split('_')[0]) === 1 ? '有り' : '無し',
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
      case 'onlyEnemyId[]':
        return {
          uuid: s.uuid,
          key: 'オンリーモンスター',
          value1: `「${OnlyEnemies[parseInt(s.value.split('_')[0])]}」`,
          value2: `${ComparisonOperators[parseInt(s.value.split('_')[1])]}`,
          enabled: s.enabled
        }
    }
  })

  const body = (
    <tbody>
      {formattedList.map((s) => (
        <tr id={s?.uuid} key={s?.uuid}>
          <td>
            <input
              type='checkbox'
              name={s?.uuid}
              value={s?.uuid}
              checked={s?.enabled}
              onChange={onChangeCheckBox}
            />
          </td>
          <td style={s?.enabled === false ? disabledFontStyle : {}}>{s?.key}</td>
          <td style={s?.enabled === false ? disabledFontStyle : {}}>{s?.value1}</td>
          <td style={s?.enabled === false ? disabledFontStyle : {}}>{s?.value2}</td>
          <td>
            <button value={s?.uuid} onClick={onClickEditButton}>
              編集
            </button>
          </td>
          <td>
            <button value={s?.uuid} onClick={onClickDeleteButton}>
              削除
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  )

  return <table>{body}</table>
}
